import { useMemo, useState } from "react";
import type { ImportRowError } from "@adva/types";
import { AlertCircle, Check, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/v2/empty-state";
import {
  useDownloadUnmatchedCsv,
  useRevalidateImportRow,
} from "@/hooks/use-import";
import { friendlyParentEntityPlural } from "../../labels";
import { buildErrorsCsv, downloadCsv } from "../labels";

interface ErrorsTabProps {
  errors: ImportRowError[];
  jobId: string;
  /** Server-side total of unmatched rows for this job. When > 0 the
   *  aggregate-sentinel callout(s) get a "Download {N} unmatched rows" CTA. */
  unmatchedCount?: number;
  entityType?: string;
  skipped: Set<number>;
  onToggleSkip: (rowIndex: number) => void;
  // ADV-1402: inline-edit state, owned by the parent (review.tsx) so the
  // approve payload is built from a single source. `corrections` maps a row
  // index to a record of edited field → value (a row can have multiple errored
  // fields); `onCorrect` records an edit. Optional so read-only / non-review
  // callers (and the existing tests) work unchanged.
  corrections?: Map<number, Record<string, string>>;
  onCorrect?: (rowIndex: number, field: string, value: string) => void;
  // ADV-1402 (R3): the per-row revalidation verdict is lifted to the parent
  // (review.tsx) so approvability can gate on confirmed-valid state — typing a
  // correction alone must NOT enable approval. Optional + internal fallback so
  // read-only / test callers that don't wire it keep working unchanged.
  verdicts?: Map<number, RowVerdict>;
  onVerdict?: (rowIndex: number, verdict: RowVerdict | null) => void;
  disabled?: boolean;
}

// Transient per-row verdict from the revalidate dry run. Pure view state — the
// durable correction lives in the parent's `corrections` map. Exported so the
// parent (review.tsx) can own it and derive approvability from confirmed-valid
// rows (ADV-1402 R3).
export type RowVerdict = "checking" | "valid" | "invalid";

export function ErrorsTab({
  errors,
  jobId,
  unmatchedCount = 0,
  entityType,
  skipped,
  onToggleSkip,
  corrections,
  onCorrect,
  verdicts: verdictsProp,
  onVerdict,
  disabled = false,
}: ErrorsTabProps) {
  const revalidate = useRevalidateImportRow(jobId);
  const [internalVerdicts, setInternalVerdicts] = useState<
    Map<number, RowVerdict>
  >(new Map());
  // Controlled when the parent supplies `verdicts`; uncontrolled otherwise.
  const verdicts = verdictsProp ?? internalVerdicts;

  const setVerdict = (row: number, v: RowVerdict | null) => {
    if (onVerdict) {
      onVerdict(row, v);
      return;
    }
    setInternalVerdicts((prev) => {
      const next = new Map(prev);
      if (v === null) next.delete(row);
      else next.set(row, v);
      return next;
    });
  };

  // Confirm an inline edit: re-validate the row server-side and record the
  // live verdict. Sends ALL of the row's edited fields together (a row can
  // have several errored fields) so the verdict reflects the whole row, not
  // just the field that was last touched. Skips when nothing is edited.
  //
  // ADV-1402 (R4b): use `mutateAsync` and resolve the verdict from the AWAITED
  // result, keyed by the row captured in this closure. The per-call
  // onSuccess/onError callbacks passed to `mutate` are only fired for the
  // LATEST mutate call on a shared mutation observer, so blurring several rows
  // in quick succession would drop or misattribute earlier rows' verdicts.
  // `mutateAsync` returns a promise tied to THIS specific call, so each row's
  // verdict lands correctly regardless of concurrency or resolution order.
  async function confirmCorrection(err: ImportRowError) {
    if (!err.field) return;
    const row = err.row;
    const rowValues = corrections?.get(row);
    if (!rowValues || Object.keys(rowValues).length === 0) {
      setVerdict(row, null);
      return;
    }
    setVerdict(row, "checking");
    try {
      const res = await revalidate.mutateAsync({ row, values: rowValues });
      setVerdict(row, res.valid ? "valid" : "invalid");
    } catch {
      setVerdict(row, "invalid");
    }
  }
  // ADV-1108: row === -1 entries are server-side aggregate sentinels (e.g.
  // "25,296 rows could not be matched on customer_external_id"). They are
  // NOT per-row errors, so we lift them out of the table and render them
  // as plain-English callouts above it.
  const { aggregate, perRow } = useMemo(() => {
    const agg: ImportRowError[] = [];
    const rows: ImportRowError[] = [];
    for (const e of errors) {
      if (e.row < 0) agg.push(e);
      else rows.push(e);
    }
    return { aggregate: agg, perRow: rows };
  }, [errors]);

  const downloadUnmatched = useDownloadUnmatchedCsv();

  function handleDownloadErrors() {
    const csv = buildErrorsCsv(perRow);
    downloadCsv(csv, `import-${jobId}-errors.csv`);
  }

  function handleDownloadUnmatched() {
    downloadUnmatched.mutate({
      jobId,
      filename: `${entityType ?? "import"}-unmatched-${jobId}.csv`,
    });
  }

  if (aggregate.length === 0 && perRow.length === 0) {
    return (
      <EmptyState title="No errors" description="Every row imported cleanly." />
    );
  }

  return (
    <div className="space-y-4">
      {aggregate.length > 0 && (
        <div className="rounded-lg border border-[color:var(--color-warning)]/40 bg-[color:var(--color-warning)]/5 p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-[color:var(--color-warning)]">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            Summary issues
          </div>
          <ul className="space-y-1 typo-body text-[color:var(--color-text-md)]">
            {aggregate.map((e, i) => (
              <li key={`${e.field}-${i}`}>{aggregateMessage(e)}</li>
            ))}
          </ul>
          {unmatchedCount > 0 && (
            <div className="mt-3">
              <Button
                size="sm"
                variant="outline"
                disabled={disabled || downloadUnmatched.isPending}
                onClick={handleDownloadUnmatched}
              >
                <Download className="size-4" /> Download{" "}
                {unmatchedCount.toLocaleString()} unmatched rows (CSV)
              </Button>
            </div>
          )}
        </div>
      )}

      {perRow.length > 0 ? (
        <>
          <div className="flex items-center justify-between">
            <p className="typo-label text-[color:var(--color-text-md)]">
              {perRow.length} {perRow.length === 1 ? "error" : "errors"}
              {skipped.size > 0 ? ` • ${skipped.size} skipped` : ""}
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownloadErrors}
              disabled={perRow.length === 0}
            >
              <Download className="size-4" /> Download error CSV
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <span className="sr-only">Skip</span>
                </TableHead>
                <TableHead className="w-20">Row</TableHead>
                <TableHead className="w-40">Field</TableHead>
                <TableHead>Error</TableHead>
                <TableHead>Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {perRow.map((err) => {
                const isSkipped = skipped.has(err.row);
                // Inline edit is available only when the row names an offending
                // field and is not skipped. Falls back to the read-only value
                // when editing isn't wired (no onCorrect) or the field is null.
                const editable =
                  !!err.field && !!onCorrect && !isSkipped && !disabled;
                const editedValue = err.field
                  ? corrections?.get(err.row)?.[err.field]
                  : undefined;
                const verdict = verdicts.get(err.row);
                const originalValue =
                  err.value == null
                    ? ""
                    : typeof err.value === "string"
                      ? err.value
                      : JSON.stringify(err.value);
                return (
                  <TableRow
                    key={`${err.row}-${err.field}`}
                    className={
                      isSkipped ? "opacity-60 line-through" : undefined
                    }
                  >
                    <TableCell>
                      <Checkbox
                        checked={isSkipped}
                        disabled={disabled}
                        onCheckedChange={() => onToggleSkip(err.row)}
                        aria-label={
                          isSkipped ? "Include this row" : "Skip this row"
                        }
                      />
                    </TableCell>
                    <TableCell className="tabular-nums">{err.row}</TableCell>
                    <TableCell className="font-mono text-[color:var(--color-text-md)]">
                      {err.field || "—"}
                    </TableCell>
                    <TableCell>{err.message}</TableCell>
                    <TableCell className="font-mono text-[color:var(--color-text-md)]">
                      {editable && err.field ? (
                        <div className="flex items-center gap-2">
                          <Input
                            className="h-8 max-w-56 font-mono"
                            defaultValue={editedValue ?? originalValue}
                            aria-label={`Correct ${err.field} for row ${err.row}`}
                            onChange={(e) =>
                              onCorrect(err.row, err.field!, e.target.value)
                            }
                            onBlur={() => void confirmCorrection(err)}
                          />
                          {verdict === "checking" && (
                            <Loader2
                              className="size-4 animate-spin text-[color:var(--color-text-md)]"
                              aria-label="Checking"
                            />
                          )}
                          {verdict === "valid" && (
                            <Badge
                              variant="outline"
                              className="border-[color:var(--color-success)]/40 text-[color:var(--color-success)]"
                            >
                              <Check className="size-3" aria-hidden="true" />{" "}
                              Valid
                            </Badge>
                          )}
                          {verdict === "invalid" && (
                            <Badge
                              variant="outline"
                              className="border-[color:var(--color-danger)]/40 text-[color:var(--color-danger)]"
                            >
                              Still invalid
                            </Badge>
                          )}
                        </div>
                      ) : originalValue === "" ? (
                        "—"
                      ) : (
                        originalValue
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </>
      ) : aggregate.length > 0 ? (
        <EmptyState
          title="No row-level errors"
          description="The summary above describes every issue with this upload."
        />
      ) : null}
    </div>
  );
}

// Turn the server-side aggregate sentinel ("Row -1 · customer_external_id
// · 25296 rows could not be matched on customer_external_id...") into the
// short, two-clause copy from ADV-1108: "{N} rows reference a {parent}
// that hasn't been imported yet."
//
// The sentinel's `message` carries the "{N} rows could not be matched on
// {field}" prefix; we re-parse the leading integer when present and fall
// back to "{Field}" + message if the shape changes.
function aggregateMessage(err: ImportRowError): string {
  const parent = err.field
    ? friendlyParentEntityPlural(err.field)
    : "parent records";
  const singular = parent.replace(/s$/, "");
  const countMatch = /^([\d,]+)\s+rows?\b/i.exec(err.message);
  if (countMatch) {
    const n = Number.parseInt(countMatch[1].replace(/,/g, ""), 10);
    if (Number.isFinite(n)) {
      return `${n.toLocaleString()} rows reference a ${singular} record that hasn't been imported yet.`;
    }
  }
  return `Some rows reference a ${singular} record that hasn't been imported yet.`;
}
